/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Components & Services
import { FixedAssetsRegisterInfo } from "./info";
import { FixedAssetsRegisterService } from "services/fixed-assets-register";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

const initialValues = {
  unit: "",
  address: "",
  year: today.getFullYear().toString(),
  assetType: "",
  details: [
    {
      ordinalNumber: "01",
      increaseDocNumber: "",
      increaseDocDate: "",
      assetName: "",
      manufacturingCountry: "",
      useMonthYear: "",
      assetId: "",
      historicalCost: 0,
      depreciationRate: 0,
      depreciationAmount: 0,
      accumulatedDepreciationUntilReduction: 0,
      decreaseDocNumber: "",
      decreaseDocDate: "",
      decreaseReason: "",
    }
  ],
  totalHistoricalCost: 0,
  totalDepreciationAmount: 0,
  totalAccumulatedDepreciation: 0,
  totalPages: "",
  openingDate: "",
  reportDate: "",
  ledgerClerk: "",
  chiefAccountant: "",
  legalRepresentative: "",
  signingDay: today.getDate().toString(),
  signingMonth: (today.getMonth() + 1).toString(),
  signingYear: today.getFullYear().toString(),
};

function FixedAssetsRegisterDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [formInitials, setFormInitials] = useState(initialValues);

  useEffect(() => {
    (async () => {
      try {
        if (id) {
          const response = await FixedAssetsRegisterService.getById(id);
          const apiData = response.data || response;

          const formatDateVN = (iso) => {
            if (!iso) return "";
            if (typeof iso === "string" && iso.includes("/")) return iso;
            const d = new Date(iso);
            return isNaN(d) ? iso : `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
          };

          let [sDay, sMonth, sYear] = [initialValues.signingDay, initialValues.signingMonth, initialValues.signingYear];
          if (apiData.reportDate?.includes("/")) {
            const pts = apiData.reportDate.split("/");
            if (pts.length === 3) [sDay, sMonth, sYear] = pts;
          }

          setFormInitials({
            ...initialValues,
            unit: apiData.unit || "",
            address: apiData.address || "",
            year: apiData.year || initialValues.year,
            assetType: apiData.assetType || "",
            details: apiData.details?.length > 0 ? apiData.details : initialValues.details,
            totalHistoricalCost: apiData.totalHistoricalCost || 0,
            totalDepreciationAmount: apiData.totalDepreciationAmount || 0,
            totalAccumulatedDepreciation: apiData.totalAccumulatedDepreciation || 0,
            totalPages: apiData.totalPages || "",
            openingDate: formatDateVN(apiData.openingDate),
            reportDate: apiData.reportDate || "",
            ledgerClerk: apiData.ledgerClerk || "",
            chiefAccountant: apiData.chiefAccountant || "",
            legalRepresentative: apiData.legalRepresentative || "",
            signingDay: sDay,
            signingMonth: sMonth,
            signingYear: sYear,
          });
        } else {
          setFormInitials(initialValues);
        }
      } catch (e) {
        console.error("Load failed:", e);
        toast.error("Không thể tải dữ liệu");
      }
    })();
  }, [id]);

  const handleBack = () => navigation("/templates/fixed-assets-register");

  const submitForm = async (fValues) => {
    try {
      const num = (v) => (v && v !== "" && !isNaN(v) ? Number(v) : 0);

      const payload = {
        unit: fValues.unit,
        address: fValues.address,
        year: fValues.year,
        assetType: fValues.assetType,
        details: (fValues.details || []).map(row => ({
          ...row,
          historicalCost: num(row.historicalCost),
          depreciationRate: num(row.depreciationRate),
          depreciationAmount: num(row.depreciationAmount),
          accumulatedDepreciationUntilReduction: num(row.accumulatedDepreciationUntilReduction),
        })),
        totalHistoricalCost: num(fValues.totalHistoricalCost),
        totalDepreciationAmount: num(fValues.totalDepreciationAmount),
        totalAccumulatedDepreciation: num(fValues.totalAccumulatedDepreciation),
        totalPages: fValues.totalPages,
        openingDate: fValues.openingDate,
        ledgerClerk: fValues.ledgerClerk,
        chiefAccountant: fValues.chiefAccountant,
        legalRepresentative: fValues.legalRepresentative,
        reportDate: `${fValues.signingDay.padStart(2, "0")}/${fValues.signingMonth.padStart(2, "0")}/${fValues.signingYear}`,
      };

      if (!id) {
        await FixedAssetsRegisterService.create(payload);
        toast.success("Tạo sổ thành công!");
      } else {
        await FixedAssetsRegisterService.update(id, payload);
        toast.success("Cập nhật thành công!");
      }
      navigation("/templates/fixed-assets-register");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Lưu thất bại!");
    }
  };

  const handlePrint = (pValues) => {
    const html = getPrintTemplate(pValues);
    const win = window.open("", "_blank");
    win.document.open();
    win.document.write(`<html><head><title>Print S21-DN</title>
      <style>@media print{@page{size:A4 landscape;margin:1cm}}body{margin:0}</style>
      </head><body onload="setTimeout(()=>[window.print(),window.close()], 500)">${html}</body></html>`);
    win.document.close();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Formik initialValues={formInitials} enableReinitialize onSubmit={submitForm}>
              {({ values, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <Card>
                    <SoftBox p={0}>
                      <FixedAssetsRegisterInfo formData={{ values, setFieldValue, containView }} />
                      <SoftBox p={3} display="flex" justifyContent="space-between" mt={2}>
                        <SoftButton variant="gradient" color="light" onClick={handleBack}>Trở về</SoftButton>
                        {containView ? (
                          <SoftButton variant="gradient" color="success" onClick={() => handlePrint(values)}>In Sổ S21-DN</SoftButton>
                        ) : (
                          <SoftButton disabled={isSubmitting} type="submit" variant="gradient" color="dark">{id ? "Lưu" : "Tạo mới"}</SoftButton>
                        )}
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default FixedAssetsRegisterDetail;
